import { useMemo, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import {
  Plus, ArrowRight, Lightbulb,
  ThumbsUp, CheckCircle2, BarChart3, Users2, AlertTriangle, CalendarClock,
  FileText, LayoutDashboard, Repeat2, X,
} from 'lucide-react'
import { WorkReport } from '@/components/WorkReport'
import { useApp } from '@/context/AppContext'
import { CreateProjectModal } from '@/components/CreateProjectModal'
import { picColor, formatDate, parsePics } from '@/lib/utils'
import type { Idea } from '@/lib/types'

// ─── Level urgensi (rule-based) ─────────────────────────────────────────────────
type UrgencyLevel = 'urgent' | 'approaching' | 'safe'
const URGENCY_META: Record<UrgencyLevel, {
  label: string; text: string; bar: string; chipBg: string; chipBorder: string
}> = {
  urgent:      { label: 'Urgent',             text: '#DC2626', bar: '#DC2626', chipBg: '#FEF2F2', chipBorder: '#FECACA' },
  approaching: { label: 'Mendekati Deadline', text: '#B45309', bar: '#F59E0B', chipBg: '#FEF3C7', chipBorder: '#FDE68A' },
  safe:        { label: 'Aman',               text: '#16A34A', bar: '#16A34A', chipBg: '#DCFCE7', chipBorder: '#BBF7D0' },
}

// ─── Tiny donut for overview ──────────────────────────────────────────────────
function OverviewDonut({ done, active, todo }: { done: number; active: number; todo: number }) {
  const total = done + active + todo
  // Segmen sesuai proporsi NYATA: hijau=selesai, oranye=aktif, abu=pending/todo.
  // Kalau tidak ada task aktif/selesai (semua pending) → abu penuh, tanpa oranye palsu.
  const doneEnd = total > 0 ? (done / total) * 100 : 0
  const activeEnd = total > 0 ? ((done + active) / total) * 100 : 0
  const pct = Math.round(doneEnd)
  return (
    <div className="relative w-20 h-20">
      <div className="w-full h-full rounded-full" style={{
        background: `conic-gradient(#16A34A 0% ${doneEnd}%, #F59E0B ${doneEnd}% ${activeEnd}%, #E2E8F0 ${activeEnd}% 100%)`,
      }} />
      <div className="absolute inset-[14px] rounded-full bg-white flex flex-col items-center justify-center">
        <span className="font-display text-[15px] font-bold leading-none" style={{ color: pct > 0 ? '#16A34A' : '#94A3B8' }}>{pct}%</span>
        <span className="text-[7px] text-[#AEADA8] font-body tracking-wider uppercase mt-0.5">done</span>
      </div>
    </div>
  )
}

// ─── Mini task bar ────────────────────────────────────────────────────────────
function TaskBar({ done, active, todo }: { done: number; active: number; todo: number }) {
  const total = done + active + todo
  if (total === 0) return <div className="h-1 bg-[#F0EFE9] rounded-full w-full" />
  return (
    <div className="flex h-1 rounded-full overflow-hidden w-full gap-px">
      {done   > 0 && <div style={{ width: `${(done/total)*100}%`,   backgroundColor: '#16A34A' }} />}
      {active > 0 && <div style={{ width: `${(active/total)*100}%`, backgroundColor: '#F59E0B' }} />}
      {todo   > 0 && <div style={{ width: `${(todo/total)*100}%`,   backgroundColor: '#E2E8F0' }} />}
    </div>
  )
}

// ─── PicAvatar ────────────────────────────────────────────────────────────────
function PicAvatar({ name }: { name: string }) {
  return (
    <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
      style={{ backgroundColor: picColor(name) }} title={name}>
      {name.charAt(0).toUpperCase()}
    </div>
  )
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ label, value, color, delay }: { label: string; value: number | string; color: string; delay: number }) {
  return (
    <div
      className={`bg-white border border-[#E8E7E3] rounded-xl px-4 py-3.5 reveal reveal-d${delay}`}
    >
      <p className="text-[9px] uppercase tracking-[0.18em] text-[#AEADA8] font-body mb-1.5">{label}</p>
      <p className="font-display font-bold leading-none" style={{ fontSize: '2rem', color }}>
        {value}
      </p>
    </div>
  )
}

// ─── Homepage ─────────────────────────────────────────────────────────────────
export default function HomePage() {
  const { projects, tasks, subtasks, loading, getEffectiveTaskStatus } = useApp()
  const [ideas, setIdeas]           = useState<Idea[]>([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [ganttPic, setGanttPic]     = useState<string | null>(null)
  const [view, setView]             = useState<'overview' | 'report'>('overview')
  const [showAllPriority, setShowAllPriority] = useState(false)
  // Hover-card untuk bar Gantt
  const [tip, setTip] = useState<{
    name: string; pct: number; complete: boolean; overdue: boolean
    endLabel: string; inProgress: string[]; color: string
    left: number; top: number; placeAbove: boolean
  } | null>(null)

  useEffect(() => {
    fetch('/api/ideas')
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setIdeas(d) })
      .catch(() => {})
  }, [])

  // Modal "lihat semua prioritas": ESC untuk tutup + kunci scroll body
  useEffect(() => {
    if (!showAllPriority) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowAllPriority(false) }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev }
  }, [showAllPriority])

  // Aggregates
  const stats = useMemo(() => {
    // Task RUTIN (repetitif) dikecualikan dari perhitungan progres (di luar %).
    const counted = tasks.filter(t => !t.recurrence)
    const done   = counted.filter(t => getEffectiveTaskStatus(t.id) === 'done').length
    const active = counted.filter(t => getEffectiveTaskStatus(t.id) === 'in-progress').length
    const todo   = counted.filter(t => getEffectiveTaskStatus(t.id) === 'not-started').length
    const pct    = counted.length > 0 ? Math.round((done / counted.length) * 100) : 0
    return { done, active, todo, pct, total: counted.length }
  }, [tasks, getEffectiveTaskStatus])

  // ── Prioritas mendesak (RULE-BASED, tanpa AI) — PROJECT saja ──
  // Urgensi = kedekatan deadline project (horizon 14 hari).
  // Project tanpa deadline tetap tampil di bawah dengan urgensi rendah.
  const priority = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0)
    const diffOf = (d: string) => {
      const dm = new Date(d); dm.setHours(0, 0, 0, 0)
      return Math.round((dm.getTime() - today.getTime()) / 86400000)
    }
    const HORIZON = 14
    // Urgent: telat/≤3 hari · Mendekati Deadline: ≤14 hari (2 minggu) · Aman: >14 hari
    const levelOf = (diff: number): UrgencyLevel =>
      diff <= 3 ? 'urgent' : diff <= 14 ? 'approaching' : 'safe'
    const daysTextOf = (diff: number) =>
      diff < 0 ? `Telat ${-diff}h` : diff === 0 ? 'Hari ini' : diff === 1 ? 'Besok' : `${diff} hari lagi`

    // Jumlah subtask per task → untuk total subtask per project
    const subCountByTask = new Map<string, number>()
    for (const s of subtasks) subCountByTask.set(s.taskId, (subCountByTask.get(s.taskId) ?? 0) + 1)

    interface U {
      key: string; title: string; sub: string; assignee: string | null
      href: string; hasDeadline: boolean; diff: number | null
      urgency: number; level: UrgencyLevel; daysText: string
    }
    const items: U[] = []

    // Project belum 100% selesai
    for (const p of projects) {
      const pt = tasks.filter((t) => t.projectId === p.id && !t.recurrence)
      const doneCount = pt.filter((t) => getEffectiveTaskStatus(t.id) === 'done').length
      const pct = pt.length > 0 ? doneCount / pt.length : 0
      if (pct >= 1) continue
      const subCount = pt.reduce((acc, t) => acc + (subCountByTask.get(t.id) ?? 0), 0)
      const sub = pt.length > 0 ? `${pt.length} task · ${subCount} subtask` : 'Belum ada task'
      if (p.deadline) {
        const diff = diffOf(p.deadline)
        const base = diff <= 0 ? 100 : Math.max(0, (100 * (HORIZON - diff)) / HORIZON)
        const urgency = Math.max(0, Math.min(100, Math.round(base)))
        items.push({
          key: `project-${p.id}`, title: p.name, sub, assignee: p.pic,
          href: `/projects/${p.id}`, hasDeadline: true, diff, urgency,
          level: diff < 0 ? 'urgent' : levelOf(diff), daysText: daysTextOf(diff),
        })
      } else {
        items.push({
          key: `project-${p.id}`, title: p.name, sub, assignee: p.pic,
          href: `/projects/${p.id}`, hasDeadline: false, diff: null,
          urgency: 10, level: 'safe', daysText: 'Tanpa deadline',
        })
      }
    }

    const withDl = items.filter((i) => i.hasDeadline)
      .sort((a, b) => b.urgency - a.urgency || (a.diff! - b.diff!))
    const without = items.filter((i) => !i.hasDeadline)
      .sort((a, b) => a.title.localeCompare(b.title))
    const ordered = [...withDl, ...without]
    const shown = ordered.slice(0, 5)
    return {
      shown,
      all: ordered,
      extra: ordered.length - shown.length,
      urgentCount: ordered.filter((i) => i.level === 'urgent').length,
      approachingCount: ordered.filter((i) => i.level === 'approaching').length,
    }
  }, [tasks, projects, subtasks, getEffectiveTaskStatus])

  // Projects yang butuh perhatian (punya task aktif)
  const activeProjects = useMemo(() =>
    projects
      .filter(p => tasks.some(t => t.projectId === p.id && getEffectiveTaskStatus(t.id) === 'in-progress'))
      .slice(0, 6)
  , [projects, tasks, getEffectiveTaskStatus])

  // Top ideas by vote
  const topIdeas = useMemo(() =>
    [...ideas].sort((a, b) => b.votes - a.votes).slice(0, 5)
  , [ideas])

  // Recent tasks completed
  const recentDone = useMemo(() =>
    tasks
      .filter(t => getEffectiveTaskStatus(t.id) === 'done')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 4)
  , [tasks, getEffectiveTaskStatus])

  // Beban kerja per anggota — assignee task, fallback ke PIC project, di-group case-insensitive
  const workload = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const picByProject = new Map(projects.map((p) => [p.id, p.pic?.trim() || '']))
    const subCountByTask = new Map<string, number>()
    for (const s of subtasks) subCountByTask.set(s.taskId, (subCountByTask.get(s.taskId) ?? 0) + 1)
    interface W { name: string; active: number; todo: number; done: number; overdue: number; subtasks: number; projects: Set<string> }
    const map = new Map<string, W>()
    for (const t of tasks) {
      // Task boleh dipegang beberapa orang. Beban kerjanya dihitung untuk SETIAP
      // PIC, karena semuanya sama-sama hands on; bukan dibagi rata.
      const pics = parsePics(t.assignee)
      const names = pics.length ? pics : [picByProject.get(t.projectId) || 'Belum ditugaskan']
      const st = getEffectiveTaskStatus(t.id)
      for (const name of names) {
        const key = name.toLowerCase()
        if (!map.has(key)) map.set(key, { name, active: 0, todo: 0, done: 0, overdue: 0, subtasks: 0, projects: new Set() })
        const e = map.get(key)!
        if (st === 'in-progress') e.active++
        else if (st === 'not-started') e.todo++
        else e.done++
        e.subtasks += subCountByTask.get(t.id) ?? 0
        e.projects.add(t.projectId)
        if (t.dueDate && st !== 'done' && new Date(t.dueDate) < today) e.overdue++
      }
    }
    return Array.from(map.values())
      .map((e) => ({ ...e, total: e.active + e.todo + e.done, projectCount: e.projects.size }))
      // Terbesar → terkecil: total task, lalu subtask, lalu task aktif, lalu nama
      .sort((a, b) =>
        b.total - a.total ||
        b.subtasks - a.subtasks ||
        b.active - a.active ||
        a.name.localeCompare(b.name),
      )
  }, [tasks, projects, subtasks, getEffectiveTaskStatus])

  // Deadline terdekat — gabungan deadline PROJECT + TASK yang belum selesai
  const deadlines = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const diffOf = (d: string) => {
      const dm = new Date(d); dm.setHours(0, 0, 0, 0)
      return Math.round((dm.getTime() - today.getTime()) / 86400000)
    }
    const fmt = (d: string) => new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })

    type Item = { kind: 'project' | 'task'; id: string; title: string; sub: string; href: string; assignee: string | null; label: string; diff: number }
    const items: Item[] = []

    // Deadline project (skip yang sudah 100% selesai)
    for (const p of projects) {
      if (!p.deadline) continue
      const pt = tasks.filter((t) => t.projectId === p.id && !t.recurrence)
      const doneCount = pt.filter((t) => getEffectiveTaskStatus(t.id) === 'done').length
      const pct = pt.length > 0 ? doneCount / pt.length : 0
      if (pct >= 1) continue
      items.push({ kind: 'project', id: p.id, title: p.name, sub: 'Project', href: `/projects/${p.id}`, assignee: p.pic, label: fmt(p.deadline), diff: diffOf(p.deadline) })
    }

    // Deadline task (belum done)
    for (const t of tasks) {
      if (!t.dueDate || getEffectiveTaskStatus(t.id) === 'done') continue
      const proj = projects.find((p) => p.id === t.projectId)
      items.push({ kind: 'task', id: t.id, title: t.title, sub: proj?.name ?? '—', href: `/projects/${t.projectId}`, assignee: t.assignee, label: fmt(t.dueDate), diff: diffOf(t.dueDate) })
    }

    return items.sort((a, b) => a.diff - b.diff).slice(0, 6)
  }, [tasks, projects, getEffectiveTaskStatus])

  // Daftar PIC yang punya project terjadwal (untuk filter timeline)
  const ganttPics = useMemo(() => {
    const set = new Set<string>()
    for (const p of projects) if (p.deadline && p.pic?.trim()) set.add(p.pic.trim())
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [projects])

  // Gantt timeline — project dengan deadline (mulai = createdAt, selesai = deadline)
  const gantt = useMemo(() => {
    const withDeadline = projects.filter(
      (p) => p.deadline && (!ganttPic || (p.pic?.trim() ?? '') === ganttPic)
    )
    if (withDeadline.length === 0) return null

    const todayMs = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime() })()
    const starts = withDeadline.map((p) => new Date(p.createdAt).getTime())
    const ends = withDeadline.map((p) => new Date(p.deadline as string).getTime())
    const pad = 3 * 86400000
    let min = Math.min(...starts, todayMs) - pad
    let max = Math.max(...ends, todayMs) + pad
    if (max <= min) max = min + 86400000
    const span = max - min

    const rows = withDeadline
      .map((p) => {
        const s = new Date(p.createdAt).getTime()
        const e = new Date(p.deadline as string).getTime()
        const pt = tasks.filter((t) => t.projectId === p.id)
        const done = pt.filter((t) => getEffectiveTaskStatus(t.id) === 'done').length
        const inProgress = pt.filter((t) => getEffectiveTaskStatus(t.id) === 'in-progress').map((t) => t.title)
        const pct = pt.length > 0 ? Math.round((done / pt.length) * 100) : 0
        const complete = pct >= 100
        return {
          project: p,
          start: s,
          end: e,
          left: ((s - min) / span) * 100,
          width: Math.max(((e - s) / span) * 100, 1.5),
          pct,
          complete,
          inProgress,
          color: complete ? '#16A34A' : p.color,   // hijau kalau 100%
          overdue: e < todayMs && !complete,
          endLabel: new Date(e).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }),
        }
      })
      .sort((a, b) => a.start - b.start)

    const ticks: { label: string; pos: number }[] = []
    const d = new Date(min); d.setDate(1); d.setHours(0, 0, 0, 0)
    while (d.getTime() <= max) {
      const pos = ((d.getTime() - min) / span) * 100
      if (pos >= 0 && pos <= 100) ticks.push({ label: d.toLocaleDateString('id-ID', { month: 'short', year: '2-digit' }), pos })
      d.setMonth(d.getMonth() + 1)
    }

    const todayPos = ((todayMs - min) / span) * 100
    const scope = ganttPic ? projects.filter((p) => (p.pic?.trim() ?? '') === ganttPic) : projects
    return { rows, ticks, todayPos, withoutDeadline: scope.length - withDeadline.length }
  }, [projects, tasks, getEffectiveTaskStatus, ganttPic])

  // Date
  const now     = new Date()
  const dayName = now.toLocaleDateString('id-ID', { weekday: 'long' }).toUpperCase()
  const dateStr = now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <div className="min-h-screen bg-[#F5F4F0]">

      {/* Sticky header */}
      <header className="bg-white border-b border-[#E8E7E3] sticky top-0 z-30">
        <div className="px-6 flex items-center gap-3" style={{ height: '52px' }}>
          <span className="font-display text-[15px] font-bold text-[#1A1916]">
            {view === 'overview' ? 'Overview' : 'Laporan Kerja'}
          </span>
          <div className="flex-1" />
          {/* Flip ke laporan kerja / kembali */}
          <button
            onClick={() => setView((v) => (v === 'overview' ? 'report' : 'overview'))}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-white border border-[#E0DFD8] text-[#1A1916] rounded-xl text-sm font-medium font-body hover:border-[#6366F1] hover:text-[#6366F1] active:scale-[0.98] transition-all group"
            title="Balik halaman"
          >
            <Repeat2 className="w-3.5 h-3.5 transition-transform group-hover:rotate-180 duration-300" />
            {view === 'overview'
              ? <><FileText className="w-3.5 h-3.5" /> Laporan Kerja</>
              : <><LayoutDashboard className="w-3.5 h-3.5" /> Overview</>}
          </button>
          {view === 'overview' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#1A1916] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2E2E2A] active:scale-[0.98] transition-all"
            >
              <Plus className="w-3.5 h-3.5" /> Buat Project
            </button>
          )}
        </div>
      </header>

      <main className="px-6 py-6">
       <div className="dash-flip-wrap">
        {/* Overview — tanpa animasi; disembunyikan (bukan di-remount) saat di Laporan Kerja */}
        <div className={view === 'report' ? 'hidden' : 'space-y-5'}>

        {/* ── HERO GREETING ──────────────────────────────────────────────────── */}
        {/* ── HERO (ramping) — identitas + ring penyelesaian ──────────────────── */}
        <div className="bg-white border border-[#E8E7E3] rounded-2xl px-6 py-5 reveal reveal-d1 flex items-center justify-between gap-6 flex-wrap">
          <div className="min-w-0">
            <p className="text-[9px] font-body font-semibold uppercase tracking-[0.25em] text-[#AEADA8] mb-2">
              {dayName} · {dateStr}
            </p>
            <h1 className="font-display text-[1.9rem] font-bold text-[#0F0F0C] leading-none">
              Sistem Project Internal <span className="text-[#6366F1]">JAL</span>
            </h1>
            <p className="text-[12px] text-[#8A8A85] font-body mt-2">
              {loading
                ? 'Memuat data...'
                : `${projects.length} project berjalan · ${stats.active} task aktif · ${ideas.length} ide terkumpul`
              }
            </p>
          </div>

          {/* Ring penyelesaian + breakdown ringkas */}
          <div className="flex items-center gap-5 flex-shrink-0">
            <div className="hidden md:flex flex-col gap-1.5 text-right">
              {[
                { label: 'Selesai',  val: stats.done,   col: '#16A34A' },
                { label: 'Aktif',    val: stats.active, col: '#F59E0B' },
                { label: 'Pending',  val: stats.todo,   col: '#94A3B8' },
              ].map(r => (
                <div key={r.label} className="flex items-center gap-2 justify-end">
                  <span className="text-[11px] text-[#8A8A85] font-body">{r.label}</span>
                  <span className="font-display text-sm font-bold w-6 text-right" style={{ color: r.col }}>{r.val}</span>
                </div>
              ))}
            </div>
            <OverviewDonut done={stats.done} active={stats.active} todo={stats.todo} />
          </div>
        </div>

        {/* ── KPI STRIP — metrik utama di atas (gaya analytics) ────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiCard label="Projects"     value={loading ? '—' : projects.length} color="#6366F1" delay={1} />
          <KpiCard label="Total Task"   value={loading ? '—' : stats.total}     color="var(--kpi-neutral)" delay={2} />
          <KpiCard label="Task Aktif"   value={loading ? '—' : stats.active}    color="#F59E0B" delay={3} />
          <KpiCard label="Task Selesai" value={loading ? '—' : stats.done}      color="#16A34A" delay={4} />
          <KpiCard label="Ide Masuk"    value={loading ? '—' : ideas.length}    color="#8B5CF6" delay={5} />
        </div>

        {/* ── PRIORITAS MENDESAK — rule-based dari deadline & prioritas ────────── */}
        <div className="bg-white border border-[#E8E7E3] rounded-2xl p-5 reveal reveal-d1">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-7 h-7 rounded-lg bg-[#FEF2F2] flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-4 h-4 text-[#DC2626]" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-[11px] uppercase tracking-[0.15em] text-[#6B6860] font-body font-semibold leading-none">
                Prioritas Mendesak
              </h2>
            </div>
            {priority.urgentCount > 0 && (
              <span className="text-[10px] font-body font-bold text-[#DC2626] bg-[#FEF2F2] border border-[#FECACA] px-2 py-0.5 rounded-md flex-shrink-0">
                {priority.urgentCount} urgent
              </span>
            )}
          </div>

          {loading ? (
            <div className="space-y-2 py-1">
              <div className="h-9 bg-[#F4F3F0] rounded-xl animate-pulse" />
              <div className="h-9 bg-[#F4F3F0] rounded-xl animate-pulse w-5/6" />
            </div>
          ) : priority.shown.length === 0 ? (
            <div className="py-8 text-center">
              <CheckCircle2 className="w-7 h-7 text-[#C0BDB6] mx-auto mb-2" />
              <p className="text-sm text-[#8A8A85] font-body">Tidak ada item mendesak</p>
              <p className="text-[11px] text-[#C0BDB6] font-body mt-1">Belum ada task / project aktif</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {priority.shown.map((it) => {
                const m = URGENCY_META[it.level]
                return (
                  <Link key={it.key} to={it.href}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#FAFAF8] border border-transparent hover:border-[#F0EFE9] transition-colors group">
                    <span className="self-stretch w-1 rounded-full flex-shrink-0" style={{ backgroundColor: m.bar }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-body font-semibold text-[#1A1916] truncate group-hover:text-[#6366F1] transition-colors">
                        {it.title}
                      </p>
                      <p className="text-[11px] text-[#8A8A85] font-body mt-0.5 truncate">
                        <span className="text-[#AEADA8]">{it.sub}</span>{it.assignee ? ` · ${it.assignee}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="flex items-center gap-1.5 text-[12px] font-body font-semibold text-[#1A1916]">
                        <CalendarClock className="w-3.5 h-3.5 text-[#AEADA8]" />{it.daysText}
                      </span>
                      <span className="text-[9px] uppercase tracking-wider font-body font-bold px-2 py-0.5 rounded-md border whitespace-nowrap"
                        style={{ color: m.text, backgroundColor: m.chipBg, borderColor: m.chipBorder }}>
                        {m.label}
                      </span>
                    </div>
                  </Link>
                )
              })}
              {priority.extra > 0 && (
                <button type="button" onClick={() => setShowAllPriority(true)}
                  className="flex items-center gap-1 px-3 pt-1.5 text-[11px] text-[#AEADA8] font-body hover:text-[#1A1916] transition-colors min-h-[32px]">
                  +{priority.extra} item lainnya <ArrowRight className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── WORKLOAD PANEL ─────────────────────────────────────────────────── */}
        {!loading && workload.length > 0 && (
          <div className="bg-white border border-[#E8E7E3] rounded-2xl p-5 reveal reveal-d2">
            <div className="flex items-center gap-2 mb-4">
              <Users2 className="w-4 h-4 text-[#6B6860]" />
              <h2 className="text-[11px] uppercase tracking-[0.15em] text-[#6B6860] font-body font-semibold">
                Beban Kerja Tim
              </h2>
              <span className="text-[10px] text-[#C0BDB6] font-body">{workload.length} orang</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {workload.map((w) => {
                const unassigned = w.name === 'Belum ditugaskan'
                const cardClass = `block border border-[#F0EFE9] rounded-xl px-3.5 py-3 bg-[#FAFAF8] transition-all ${
                  unassigned ? '' : 'hover:border-[#C8C7BF] hover:bg-white hover:shadow-sm cursor-pointer group'
                }`
                const inner = (
                  <>
                    <div className="flex items-center gap-2.5 mb-2.5">
                      {unassigned ? (
                        <div className="w-7 h-7 rounded-full bg-[#EEEEE9] flex items-center justify-center flex-shrink-0">
                          <Users2 className="w-3.5 h-3.5 text-[#C0BDB6]" />
                        </div>
                      ) : (
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                          style={{ backgroundColor: picColor(w.name) }}>
                          {w.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-body font-semibold text-[#1A1916] truncate">{w.name}</p>
                        <p className="text-[10px] text-[#AEADA8] font-body truncate">
                          {w.total} task · {w.subtasks} subtask · {w.projectCount} project
                        </p>
                      </div>
                      {w.overdue > 0 && (
                        <span className="flex items-center gap-1 text-[10px] font-body font-semibold text-[#DC2626] bg-[#FEF2F2] border border-[#FECACA] px-1.5 py-0.5 rounded-md flex-shrink-0"
                          title={`${w.overdue} task telat`}>
                          <AlertTriangle className="w-3 h-3" /> {w.overdue}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-[11px] font-body">
                      <span className="flex items-center gap-1 text-[#B45309]">
                        <span className="w-1.5 h-1.5 rounded-sm bg-[#F59E0B] inline-block" />
                        <span className="font-display font-bold">{w.active}</span> aktif
                      </span>
                      <span className="flex items-center gap-1 text-[#64748B]">
                        <span className="w-1.5 h-1.5 rounded-sm bg-[#E2E8F0] inline-block" />
                        <span className="font-display font-bold">{w.todo}</span> todo
                      </span>
                      <span className="flex items-center gap-1 text-[#15803D]">
                        <span className="w-1.5 h-1.5 rounded-sm bg-[#16A34A] inline-block" />
                        <span className="font-display font-bold">{w.done}</span> done
                      </span>
                    </div>
                  </>
                )
                return unassigned ? (
                  <div key={w.name} className={cardClass}>{inner}</div>
                ) : (
                  <Link key={w.name} to={`/projects?pic=${encodeURIComponent(w.name)}`} className={cardClass}>
                    {inner}
                  </Link>
                )
              })}
            </div>
          </div>
        )}

        {/* ── GANTT TIMELINE ─────────────────────────────────────────────────── */}
        {!loading && (
          <div className="bg-white border border-[#E8E7E3] rounded-2xl p-5 reveal reveal-d3">
            <div className="flex items-center gap-2 mb-4">
              <CalendarClock className="w-4 h-4 text-[#6B6860]" />
              <h2 className="text-[11px] uppercase tracking-[0.15em] text-[#6B6860] font-body font-semibold">
                Timeline Project
              </h2>
              {gantt && <span className="text-[10px] text-[#C0BDB6] font-body">{gantt.rows.length} project terjadwal</span>}
              {ganttPics.length > 0 && (
                <select
                  value={ganttPic ?? ''}
                  onChange={(e) => setGanttPic(e.target.value || null)}
                  className="ml-auto text-[11px] font-body text-[#6B6860] bg-[#F8F7F3] border border-[#E0DFD8] rounded-lg px-2.5 py-1 focus:outline-none focus:border-[#1A1916] cursor-pointer"
                >
                  <option value="">Semua PIC</option>
                  {ganttPics.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              )}
            </div>

            {!gantt ? (
              <div className="py-10 text-center">
                <p className="text-sm text-[#8A8A85] font-body">Belum ada project dengan deadline</p>
                <p className="text-[11px] text-[#C0BDB6] font-body mt-1">Set deadline project di Project Management untuk lihat timeline-nya di sini</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <div className="flex min-w-[640px]">
                    {/* Label column */}
                    <div className="w-40 shrink-0">
                      <div className="h-6" />
                      {gantt.rows.map((r) => (
                        <Link key={r.project.id} to={`/projects/${r.project.id}`}
                          className="h-9 flex items-center pr-3 group/row hover:bg-[#FAFAF8] rounded-lg -ml-1 pl-1 transition-colors">
                          <span className="w-2 h-2 rounded-full flex-shrink-0 mr-2" style={{ backgroundColor: r.color }} />
                          <span className="text-[12px] font-body text-[#3A3A36] truncate group-hover/row:text-[#6366F1] transition-colors">{r.project.name}</span>
                        </Link>
                      ))}
                    </div>

                    {/* Track area */}
                    <div className="flex-1 relative">
                      {/* Axis header */}
                      <div className="h-6 relative border-b border-[#F0EFE9]">
                        {gantt.ticks.map((t, i) => (
                          <span key={i} className="absolute top-0 text-[9px] text-[#C0BDB6] font-body -translate-x-1/2 whitespace-nowrap"
                            style={{ left: `${t.pos}%` }}>
                            {t.label}
                          </span>
                        ))}
                      </div>

                      {/* Rows + overlays */}
                      <div className="relative">
                        {/* Month gridlines */}
                        {gantt.ticks.map((t, i) => (
                          <div key={i} className="absolute top-0 bottom-0 w-px bg-[#F4F3F0]" style={{ left: `${t.pos}%` }} />
                        ))}
                        {/* Today line */}
                        {gantt.todayPos >= 0 && gantt.todayPos <= 100 && (
                          <div className="absolute top-0 bottom-0 w-0.5 bg-[#6366F1]/50 z-10" style={{ left: `${gantt.todayPos}%` }}>
                            <span
                              className={`absolute -top-0 text-[8px] font-body font-bold text-[#6366F1] whitespace-nowrap ${gantt.todayPos > 80 ? 'right-1 text-right' : 'left-1'}`}
                            >
                              {now.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                          </div>
                        )}

                        {gantt.rows.map((r) => (
                          <div key={r.project.id} className="h-9 relative">
                            <Link
                              to={`/projects/${r.project.id}`}
                              onMouseEnter={(e) => {
                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                                const placeAbove = rect.top > window.innerHeight / 2
                                setTip({
                                  name: r.project.name, pct: r.pct, complete: r.complete, overdue: r.overdue,
                                  endLabel: r.endLabel, inProgress: r.inProgress, color: r.color,
                                  left: Math.min(Math.max(8, rect.left), window.innerWidth - 312),
                                  top: placeAbove ? rect.top - 8 : rect.bottom + 8,
                                  placeAbove,
                                })
                              }}
                              onMouseLeave={() => setTip(null)}
                              className={`absolute top-2 h-5 rounded-full overflow-hidden flex items-center cursor-pointer hover:ring-2 hover:ring-[#6366F1]/40 transition-shadow ${r.overdue ? 'ring-1 ring-[#DC2626]' : ''}`}
                              style={{ left: `${r.left}%`, width: `${r.width}%`, backgroundColor: `${r.color}33` }}
                            >
                              <div className="h-full rounded-full" style={{ width: `${r.pct}%`, backgroundColor: r.color }} />
                              {/* Label persen — chip putih + teks warna project, selalu terbaca
                                  (baik di atas area terang maupun bar terisi). */}
                              <span
                                className="absolute right-1 top-1/2 -translate-y-1/2 text-[9px] font-body font-bold leading-none px-1 py-px rounded"
                                style={{ color: r.color, backgroundColor: 'rgba(255,255,255,0.9)' }}
                              >
                                {r.pct}%
                              </span>
                            </Link>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Legend */}
                <div className="flex items-center gap-4 mt-4 pt-3 border-t border-[#F4F3F0] text-[10px] font-body text-[#8A8A85]">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-full bg-[#16A34A] inline-block" /> 100% selesai</span>
                  <span className="flex items-center gap-1.5"><span className="w-0.5 h-3 bg-[#6366F1]/50 inline-block" /> Hari ini</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-full ring-1 ring-[#DC2626] inline-block" /> Telat</span>
                  {gantt.withoutDeadline > 0 && (
                    <span className="ml-auto text-[#C0BDB6]">{gantt.withoutDeadline} project tanpa deadline (tidak ditampilkan)</span>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── MAIN GRID ──────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-5">

          {/* Left: Active projects */}
          <div className="bg-white border border-[#E8E7E3] rounded-2xl overflow-hidden reveal reveal-d2">
            <div className="px-5 py-4 border-b border-[#F4F3F0] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-[#6B6860]" />
                <h2 className="text-[11px] uppercase tracking-[0.15em] text-[#6B6860] font-body font-semibold">
                  Sedang Berjalan
                </h2>
                <span className="text-[10px] text-[#C0BDB6] font-body">{activeProjects.length}</span>
              </div>
              <Link to="/projects"
                className="flex items-center gap-1 text-[11px] text-[#AEADA8] font-body hover:text-[#1A1916] transition-colors">
                Lihat semua <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            {activeProjects.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center px-6">
                <CheckCircle2 className="w-8 h-8 text-[#C0BDB6] mb-3" />
                <p className="text-sm text-[#8A8A85] font-body">Semua project sudah selesai 🎉</p>
                <Link to="/projects"
                  className="mt-3 text-xs font-body font-semibold text-[#6366F1] hover:underline">
                  Buat project baru →
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-[#F8F7F5]">
                {activeProjects.map((p, idx) => {
                  const pt      = tasks.filter(t => t.projectId === p.id)
                  const done    = pt.filter(t => getEffectiveTaskStatus(t.id) === 'done').length
                  const active  = pt.filter(t => getEffectiveTaskStatus(t.id) === 'in-progress').length
                  const todo    = pt.filter(t => getEffectiveTaskStatus(t.id) === 'not-started').length
                  const pct     = pt.length > 0 ? Math.round((done / pt.length) * 100) : 0
                  return (
                    <Link key={p.id} to={`/projects/${p.id}`}
                      className={`flex items-center gap-4 px-5 py-3.5 hover:bg-[#FAFAF8] transition-colors group reveal reveal-d${Math.min(idx + 2, 5)}`}>
                      {/* Color dot */}
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />

                      {/* Name + bar */}
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-body font-semibold text-[#1A1916] truncate mb-1.5">
                          {p.name}
                        </p>
                        <TaskBar done={done} active={active} todo={todo} />
                      </div>

                      {/* Task counts */}
                      <div className="flex items-center gap-2.5 text-[11px] font-display font-bold flex-shrink-0">
                        <span style={{ color: '#16A34A' }}>{done}</span>
                        <span style={{ color: '#F59E0B' }}>{active}</span>
                        <span style={{ color: '#94A3B8' }}>{todo}</span>
                      </div>

                      {/* PIC */}
                      {p.pic && <PicAvatar name={p.pic} />}

                      {/* Percent on hover */}
                      <span className="text-[11px] font-body font-semibold text-[#1A1916] w-8 text-right opacity-0 group-hover:opacity-50 transition-opacity">
                        {pct}%
                      </span>
                    </Link>
                  )
                })}
              </div>
            )}

            {/* Recently completed tasks */}
            {recentDone.length > 0 && (
              <div className="px-5 pb-4 pt-3 border-t border-[#F4F3F0]">
                <p className="text-[9px] uppercase tracking-[0.15em] text-[#C0BDB6] font-body mb-2.5 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3" /> Baru Diselesaikan
                </p>
                <div className="space-y-1.5">
                  {recentDone.map(t => {
                    const p = projects.find(p => p.id === t.projectId)
                    return (
                      <div key={t.id} className="flex items-center gap-2.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#16A34A] flex-shrink-0" />
                        <span className="text-[12px] text-[#6B6860] font-body truncate flex-1">{t.title}</span>
                        {p && <span className="text-[10px] text-[#C0BDB6] font-body flex-shrink-0 truncate max-w-[100px]">{p.name}</span>}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right column */}
          <div className="space-y-4">

            {/* Deadline terdekat */}
            <div className="bg-white border border-[#E8E7E3] rounded-2xl overflow-hidden reveal reveal-d3">
              <div className="px-5 py-4 border-b border-[#F4F3F0] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CalendarClock className="w-4 h-4 text-[#6B6860]" />
                  <h2 className="text-[11px] uppercase tracking-[0.15em] text-[#6B6860] font-body font-semibold">
                    Deadline Terdekat
                  </h2>
                  <span className="text-[10px] text-[#C0BDB6] font-body">{deadlines.length}</span>
                </div>
                <Link to="/projects"
                  className="flex items-center gap-1 text-[11px] text-[#AEADA8] font-body hover:text-[#1A1916] transition-colors">
                  Projects <ArrowRight className="w-3 h-3" />
                </Link>
              </div>

              {deadlines.length === 0 ? (
                <div className="px-5 py-6 text-center">
                  <p className="text-sm text-[#AEADA8] font-body">Belum ada task dengan deadline</p>
                  <p className="text-[11px] text-[#C0BDB6] font-body mt-1">Set deadline lewat Edit Task di Project Management</p>
                </div>
              ) : (
                <div className="divide-y divide-[#F8F7F5]">
                  {deadlines.map((it) => {
                    const overdue = it.diff < 0
                    const soon = it.diff >= 0 && it.diff <= 2
                    const note = overdue ? `Telat ${Math.abs(it.diff)}h` : it.diff === 0 ? 'Hari ini' : it.diff === 1 ? 'Besok' : it.label
                    return (
                      <Link key={`${it.kind}-${it.id}`} to={it.href}
                        className="flex items-center gap-3 px-5 py-3 hover:bg-[#FAFAF8] transition-colors group">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            {it.kind === 'project' && (
                              <span className="text-[8px] uppercase tracking-wider font-body font-bold text-[#6366F1] bg-[#EEF2FF] px-1.5 py-0.5 rounded flex-shrink-0">
                                Project
                              </span>
                            )}
                            <p className="text-[13px] font-body font-medium text-[#1A1916] truncate group-hover:text-[#6366F1] transition-colors">
                              {it.title}
                            </p>
                          </div>
                          <p className="text-[10px] text-[#AEADA8] font-body truncate mt-0.5">
                            {it.sub}{it.assignee ? ` · ${it.assignee}` : ''}
                          </p>
                        </div>
                        <span className={`flex items-center gap-1 text-[10px] font-body font-semibold px-2 py-1 rounded-md flex-shrink-0 ${
                          overdue ? 'bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]'
                          : soon ? 'bg-[#FEF3C7] text-[#B45309] border border-[#FDE68A]'
                          : 'bg-[#F4F3F0] text-[#6B6860]'
                        }`}>
                          <CalendarClock className="w-3 h-3" />
                          {note}
                        </span>
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Top ideas */}
            <div className="bg-white border border-[#E8E7E3] rounded-2xl overflow-hidden reveal reveal-d4">
              <div className="px-5 py-4 border-b border-[#F4F3F0] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-[#8B5CF6]" />
                  <h2 className="text-[11px] uppercase tracking-[0.15em] text-[#6B6860] font-body font-semibold">
                    Ide Terpopuler
                  </h2>
                </div>
                <Link to="/ideas"
                  className="flex items-center gap-1 text-[11px] text-[#AEADA8] font-body hover:text-[#1A1916] transition-colors">
                  Semua <ArrowRight className="w-3 h-3" />
                </Link>
              </div>

              {topIdeas.length === 0 ? (
                <div className="px-5 py-6 text-center">
                  <p className="text-sm text-[#AEADA8] font-body">Belum ada ide</p>
                  <Link to="/ideas" className="mt-2 text-xs font-body font-semibold text-[#8B5CF6] hover:underline block">
                    Submit ide pertama →
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-[#F8F7F5]">
                  {topIdeas.map((idea, i) => (
                    <Link key={idea.id} to="/ideas"
                      className="flex items-start gap-3 px-5 py-3 hover:bg-[#FAFAF8] transition-colors group">
                      <span className="text-[11px] font-display font-bold text-[#AEADA8] w-4 flex-shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-body font-semibold text-[#1A1916] line-clamp-1 group-hover:text-[#6366F1] transition-colors">
                          {idea.title}
                        </p>
                        <p className="text-[10px] text-[#AEADA8] font-body mt-0.5">{idea.category}</p>
                      </div>
                      <div className="flex items-center gap-1 text-[11px] font-body font-bold text-[#8B5CF6] flex-shrink-0 mt-0.5">
                        <ThumbsUp className="w-3 h-3" />
                        {idea.votes}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Progress summary */}
            <div className="bg-white border border-[#E8E7E3] rounded-2xl p-5 reveal reveal-d5">
              <h2 className="text-[11px] uppercase tracking-[0.15em] text-[#6B6860] font-body font-semibold mb-3">
                Ringkasan Progres
              </h2>
              <div className="space-y-3">
                {projects.slice(0, 4).map(p => {
                  const pt   = tasks.filter(t => t.projectId === p.id)
                  const done = pt.filter(t => getEffectiveTaskStatus(t.id) === 'done').length
                  const pct  = pt.length > 0 ? Math.round((done / pt.length) * 100) : 0
                  return (
                    <div key={p.id}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-[11px] font-body text-[#6B6860] truncate flex-1 mr-2">{p.name}</span>
                        <span className="text-[11px] font-body font-semibold" style={{ color: p.color }}>{pct}%</span>
                      </div>
                      <div className="h-1 bg-[#F0EFE9] rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700"
                          style={{ width: `${pct}%`, backgroundColor: p.color }} />
                      </div>
                    </div>
                  )
                })}
                {projects.length > 4 && (
                  <Link to="/projects" className="text-[10px] text-[#AEADA8] font-body hover:text-[#1A1916] transition-colors flex items-center gap-1">
                    +{projects.length - 4} project lainnya <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
        </div>

        {/* Laporan Kerja — animasi flip HANYA di sini, saat switch ke laporan */}
        {view === 'report' && (
          <div key="report" className="dash-face">
            <WorkReport />
          </div>
        )}
       </div>
      </main>

      {showCreateModal && <CreateProjectModal onClose={() => setShowCreateModal(false)} />}

      {/* Hover-card Gantt — portal ke body biar tidak terpotong/terdistorsi */}
      {/* ── MODAL: SEMUA PRIORITAS MENDESAK ──────────────────────────────────── */}
      {showAllPriority && createPortal(
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/40 backdrop-blur-[2px] animate-fade-in"
          onClick={() => setShowAllPriority(false)}
          role="dialog" aria-modal="true" aria-label="Semua prioritas mendesak"
        >
          <div
            className="bg-white w-full sm:max-w-2xl max-h-[85vh] sm:max-h-[80vh] rounded-t-2xl sm:rounded-2xl shadow-2xl shadow-black/20 flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header sticky */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-[#F0EFE9] flex-shrink-0">
              <div className="w-8 h-8 rounded-lg bg-[#FEF2F2] flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-4 h-4 text-[#DC2626]" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-[15px] font-display font-bold text-[#1A1916] leading-tight">Prioritas Mendesak</h2>
                <p className="text-[11px] text-[#8A8A85] font-body mt-0.5">
                  {priority.all.length} project aktif
                  {priority.urgentCount > 0 && <span className="text-[#DC2626] font-semibold"> · {priority.urgentCount} urgent</span>}
                  {priority.approachingCount > 0 && <span className="text-[#B45309] font-semibold"> · {priority.approachingCount} mendekati deadline</span>}
                </p>
              </div>
              <button type="button" onClick={() => setShowAllPriority(false)} aria-label="Tutup"
                className="w-9 h-9 rounded-lg flex items-center justify-center text-[#8A8A85] hover:bg-[#F4F3F0] hover:text-[#1A1916] transition-colors flex-shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Daftar lengkap (scroll) */}
            <div className="overflow-y-auto px-3 py-3 space-y-1.5">
              {priority.all.map((it) => {
                const m = URGENCY_META[it.level]
                return (
                  <Link key={it.key} to={it.href} onClick={() => setShowAllPriority(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#FAFAF8] border border-transparent hover:border-[#F0EFE9] transition-colors group">
                    <span className="self-stretch w-1 rounded-full flex-shrink-0" style={{ backgroundColor: m.bar }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-body font-semibold text-[#1A1916] truncate group-hover:text-[#6366F1] transition-colors">
                        {it.title}
                      </p>
                      <p className="text-[11px] text-[#8A8A85] font-body mt-0.5 truncate">
                        <span className="text-[#AEADA8]">{it.sub}</span>{it.assignee ? ` · ${it.assignee}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="flex items-center gap-1.5 text-[12px] font-body font-semibold text-[#1A1916]">
                        <CalendarClock className="w-3.5 h-3.5 text-[#AEADA8]" />{it.daysText}
                      </span>
                      <span className="text-[9px] uppercase tracking-wider font-body font-bold px-2 py-0.5 rounded-md border whitespace-nowrap"
                        style={{ color: m.text, backgroundColor: m.chipBg, borderColor: m.chipBorder }}>
                        {m.label}
                      </span>
                    </div>
                  </Link>
                )
              })}
            </div>
          </div>
        </div>,
        document.body,
      )}

      {tip && createPortal(
        <div
          style={{ position: 'fixed', left: tip.left, top: tip.top, width: 300, transform: tip.placeAbove ? 'translateY(-100%)' : undefined }}
          className="z-[60] pointer-events-none bg-white border border-[#E8E7E3] rounded-xl shadow-2xl shadow-black/20 p-3.5 animate-fade-in"
        >
          {/* Header */}
          <div className="flex items-start gap-2 mb-2.5">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: tip.color }} />
            <p className="text-[13px] font-display font-bold text-[#1A1916] leading-snug flex-1">{tip.name}</p>
            <span
              className="text-[9px] uppercase tracking-wider font-body font-bold px-2 py-0.5 rounded-md border flex-shrink-0"
              style={
                tip.complete ? { color: '#16A34A', backgroundColor: '#DCFCE7', borderColor: '#BBF7D0' }
                : tip.overdue ? { color: '#DC2626', backgroundColor: '#FEF2F2', borderColor: '#FECACA' }
                : { color: '#B45309', backgroundColor: '#FEF3C7', borderColor: '#FDE68A' }
              }
            >
              {tip.complete ? 'Selesai' : tip.overdue ? 'Telat' : 'Berjalan'}
            </span>
          </div>

          {/* Progress + deadline */}
          <div className="flex items-center gap-2 mb-1.5">
            <div className="flex-1 h-1.5 bg-[#F0EFE9] rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${tip.pct}%`, backgroundColor: tip.color }} />
            </div>
            <span className="text-[11px] font-display font-bold w-9 text-right" style={{ color: tip.color }}>{tip.pct}%</span>
          </div>
          <p className="flex items-center gap-1 text-[10px] text-[#8A8A85] font-body mb-2.5">
            <CalendarClock className="w-3 h-3" /> Deadline {tip.endLabel}
          </p>

          {/* Sedang dikerjakan */}
          <div className="pt-2.5 border-t border-[#F4F3F0]">
            <p className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-[#C0BDB6] font-body font-semibold mb-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" />
              Sedang dikerjakan{tip.inProgress.length ? ` (${tip.inProgress.length})` : ''}
            </p>
            {tip.inProgress.length === 0 ? (
              <p className="text-[11px] text-[#AEADA8] font-body">Belum ada task yang berjalan</p>
            ) : (
              <ul className="space-y-1">
                {tip.inProgress.slice(0, 5).map((t, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[11px] text-[#3A3A36] font-body leading-snug">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] mt-1.5 flex-shrink-0" />
                    <span className="flex-1">{t}</span>
                  </li>
                ))}
                {tip.inProgress.length > 5 && (
                  <li className="text-[10px] text-[#AEADA8] font-body pl-3">+{tip.inProgress.length - 5} task lagi</li>
                )}
              </ul>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
